// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — TipTap Editor Toolbar (Complete)
// ═══════════════════════════════════════════════════════════════

import { useState, useRef, useCallback } from 'react';
import { DOC_COLORS, EDITOR_PARAGRAPH_OPTIONS } from '../../../../../config/documentStyles';
import { useI18n } from '../../../../../i18n';
import { CrossRefPopover } from '../editor/CrossRefPopover';

export interface EditorToolbarProps {
  editor: any;
  onImportDocx?: () => void;
  onToggleVIA?: () => void;
}

const SEP = () => (
  <span style={{ width: 1, height: 20, background: '#d1d5db', margin: '0 4px', flexShrink: 0 }} />
);

export function EditorToolbar({ editor, onImportDocx, onToggleVIA }: EditorToolbarProps) {
  const { t } = useI18n();
  const [crossRefOpen, setCrossRefOpen] = useState(false);
  const [crossRefAnchor, setCrossRefAnchor] = useState<DOMRect | null>(null);
  const crossRefBtnRef = useRef<HTMLButtonElement>(null);

  const handleCrossRefOpen = useCallback(() => {
    if (crossRefBtnRef.current) {
      setCrossRefAnchor(crossRefBtnRef.current.getBoundingClientRect());
    }
    setCrossRefOpen(true);
  }, []);

  const handleCrossRefSelect = useCallback(
    (doc: { id: string; codigo: string; nombre: string }) => {
      if (!editor) return;
      const displayText = doc.nombre ? `${doc.codigo} ${doc.nombre}` : doc.codigo;
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text: displayText,
          marks: [
            {
              type: 'referenciaCruzada',
              attrs: {
                documentoId: doc.id || null,
                codigo: doc.codigo,
                nombre: doc.nombre,
                estadoVerificacion: doc.id ? 'valida' : 'pendiente',
              },
            },
          ],
        })
        .run();
      setCrossRefOpen(false);
    },
    [editor],
  );

  if (!editor) return null;

  const tb = (active: boolean): React.CSSProperties => ({
    padding: '4px 10px',
    borderRadius: 4,
    border: '1px solid #d1d5db',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
    background: active ? DOC_COLORS.h2 : 'white',
    color: active ? 'white' : '#374151',
  });

  const tbAction: React.CSSProperties = {
    padding: '4px 10px',
    borderRadius: 4,
    border: '1px solid #d1d5db',
    fontSize: 11,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
    background: 'white',
    color: '#2F5496',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  };

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 4,
        padding: '6px 16px',
        background: '#f8f9fc',
        borderBottom: '1px solid #e2e8f0',
      }}
    >
      {/* Undo / Redo */}
      <button
        style={tb(false)}
        onClick={() => editor.chain().focus().undo().run()}
        title={t.editor.toolbar.undo ?? 'Deshacer'}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="1 4 1 10 7 10" />
          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
        </svg>
      </button>
      <button
        style={tb(false)}
        onClick={() => editor.chain().focus().redo().run()}
        title={t.editor.toolbar.redo ?? 'Rehacer'}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="23 4 23 10 17 10" />
          <path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" />
        </svg>
      </button>
      <SEP />

      {/* Paragraph style selector */}
      <select
        style={{
          padding: '4px 8px',
          borderRadius: 4,
          border: '1px solid #d1d5db',
          fontSize: 12,
          fontFamily: 'inherit',
          cursor: 'pointer',
          maxWidth: 180,
        }}
        value={
          editor.isActive('heading', { level: 1 })
            ? 'h1'
            : editor.isActive('heading', { level: 2 })
              ? 'h2'
              : editor.isActive('heading', { level: 3 })
                ? 'h3'
                : editor.isActive('heading', { level: 4 })
                  ? 'h4'
                  : 'p'
        }
        onChange={(e) => {
          const v = e.target.value;
          if (v === 'h1') editor.chain().focus().toggleHeading({ level: 1 }).run();
          else if (v === 'h2') editor.chain().focus().toggleHeading({ level: 2 }).run();
          else if (v === 'h3') editor.chain().focus().toggleHeading({ level: 3 }).run();
          else if (v === 'h4') editor.chain().focus().toggleHeading({ level: 4 }).run();
          else editor.chain().focus().setParagraph().run();
        }}
      >
        {EDITOR_PARAGRAPH_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <SEP />

      {/* Inline marks */}
      <button
        style={tb(editor.isActive('bold'))}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title={t.editor.toolbar.bold ?? 'Negrita'}
      >
        <strong>B</strong>
      </button>
      <button
        style={tb(editor.isActive('italic'))}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title={t.editor.toolbar.italic ?? 'Cursiva'}
      >
        <em>I</em>
      </button>
      <button
        style={tb(editor.isActive('underline'))}
        onClick={() => editor.chain().focus().toggleUnderline?.().run?.()}
        title={t.editor.toolbar.underline ?? 'Subrayado'}
      >
        <u>U</u>
      </button>
      <SEP />

      {/* Lists */}
      <button
        style={tb(editor.isActive('bulletList'))}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title={t.editor.toolbar.bulletList ?? 'Lista con viñetas'}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <circle cx="3" cy="6" r="1.5" fill="currentColor" />
          <circle cx="3" cy="12" r="1.5" fill="currentColor" />
          <circle cx="3" cy="18" r="1.5" fill="currentColor" />
        </svg>
      </button>
      <button
        style={tb(editor.isActive('orderedList'))}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title={t.editor.toolbar.orderedList ?? 'Lista numerada'}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <line x1="10" y1="6" x2="21" y2="6" />
          <line x1="10" y1="12" x2="21" y2="12" />
          <line x1="10" y1="18" x2="21" y2="18" />
          <text x="2" y="8" fill="currentColor" fontSize="7" fontWeight="700">
            1
          </text>
          <text x="2" y="14" fill="currentColor" fontSize="7" fontWeight="700">
            2
          </text>
          <text x="2" y="20" fill="currentColor" fontSize="7" fontWeight="700">
            3
          </text>
        </svg>
      </button>
      <SEP />

      {/* Table */}
      <button
        style={tb(false)}
        onClick={() =>
          editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
        }
        title={t.editor.toolbar.table ?? 'Insertar tabla'}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="3" y1="15" x2="21" y2="15" />
          <line x1="9" y1="3" x2="9" y2="21" />
          <line x1="15" y1="3" x2="15" y2="21" />
        </svg>
      </button>
      <SEP />

      {/* Reference + Image */}
      <button
        ref={crossRefBtnRef}
        style={tb(crossRefOpen)}
        onClick={handleCrossRefOpen}
        title={t.editor.toolbar.crossRef ?? 'Referencia cruzada'}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      </button>
      <CrossRefPopover
        open={crossRefOpen}
        anchorRect={crossRefAnchor}
        onClose={() => setCrossRefOpen(false)}
        onSelect={handleCrossRefSelect}
      />
      <button
        style={tb(false)}
        onClick={() => {
          const url = prompt('URL de la imagen:');
          if (url) editor.chain().focus().setImage({ src: url }).run();
        }}
        title={t.editor.toolbar.image ?? 'Insertar imagen'}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      </button>
      <SEP />

      {/* Callout + Horizontal rule */}
      <button
        style={tb(false)}
        onClick={() => {
          editor
            .chain()
            .focus()
            .insertContent({
              type: 'callout',
              attrs: { type: 'info' },
              content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Nota normativa' }] }],
            })
            .run();
        }}
        title={t.editor.toolbar.normNote ?? 'Nota normativa'}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </button>
      <button
        style={tb(false)}
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title={t.editor.toolbar.hr ?? 'Línea horizontal'}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <line x1="3" y1="12" x2="21" y2="12" />
        </svg>
      </button>
      <SEP />

      {/* Import DOCX */}
      {onImportDocx && (
        <button
          style={tbAction}
          onClick={onImportDocx}
          title={t.editor.toolbar.importDocx ?? 'Importar DOCX'}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          DOCX
        </button>
      )}

      {/* Spacer + V·IA toggle */}
      <div style={{ flex: 1 }} />
      {onToggleVIA && (
        <button
          style={{
            padding: '4px 12px',
            borderRadius: 6,
            border: 'none',
            background: '#1F3864',
            color: '#33BCE1',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            transition: 'all 0.15s',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
          onClick={onToggleVIA}
          title="VALOR·IA Assist"
        >
          ✦ V·IA
        </button>
      )}
    </div>
  );
}
